/**
 * Knowledge Packs queue — Phase 12 admin/governance.
 */

import { FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, KnowledgePackListItem, KnowledgeControlStats } from '../lib/api'
import {
  KnowledgeControlAuthError,
  clearKnowledgeAdminToken,
  loadKnowledgeAdminToken,
  resolveKnowledgeAdminAccess,
  saveKnowledgeAdminToken,
} from '../lib/knowledgeControlAuth'
import { LoadingSpinner } from '../components/design-system/Loading'
import { EmptyState } from '../components/design-system/EmptyState'
import { Badge } from '../components/design-system/Badge'
import { Button } from '../components/design-system/Button'
import { Modal } from '../components/design-system/Modal'
import { useLocale } from '../contexts/LocaleContext'
import {
  formatLabel,
  lifecycleLabel,
  packDisplayName,
  tagLabel,
  validationLabel,
} from '../lib/i18n'

const GROUP_VALUES = ['', 'CORE', 'WEB', 'API', 'AUTH', 'IAC', 'CLOUD', 'LANGUAGE', 'AI', 'HMVM_META']
const STATUS_VALUES = ['', 'draft', 'testing', 'approved', 'active', 'deprecated', 'rejected']

function statusTone(status: string): 'warning' | 'info' | 'success' | 'danger' | 'default' {
  if (status === 'draft' || status === 'testing') return 'warning'
  if (status === 'approved' || status === 'active') return 'success'
  if (status === 'rejected') return 'danger'
  if (status === 'deprecated') return 'default'
  return 'info'
}

export function KnowledgePacksPage() {
  const { t, locale } = useLocale()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tokenDraft, setTokenDraft] = useState('')
  const [hasToken, setHasToken] = useState<boolean | null>(() => (loadKnowledgeAdminToken() ? true : null))
  const [unauthorized, setUnauthorized] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<KnowledgePackListItem[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [stats, setStats] = useState<KnowledgeControlStats | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createForm, setCreateForm] = useState({
    pack_id: '',
    version: '1.0.0',
    name: '',
    description: '',
    format_type: 'dockerfile',
  })

  const search = searchParams.get('search') || ''
  const status = searchParams.get('status') || ''
  const tag = searchParams.get('tag') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)
  const pageSize = 50

  useEffect(() => {
    if (hasToken !== null) return
    let cancelled = false
    void resolveKnowledgeAdminAccess(() => api.me()).then((ok) => {
      if (!cancelled) setHasToken(ok)
    })
    return () => {
      cancelled = true
    }
  }, [hasToken])

  useEffect(() => {
    if (hasToken !== true) {
      if (hasToken === false) {
        setLoading(false)
        setUnauthorized(true)
      }
      return
    }
    void loadQueue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken, search, status, tag, page])

  async function loadQueue() {
    try {
      setLoading(true)
      setError(null)
      setUnauthorized(false)
      const listParams: Parameters<typeof api.listKnowledgePacks>[0] = {
        search_query: search || undefined,
        tag: tag || undefined,
        page,
        page_size: pageSize,
      }
      if (status) listParams.status = status
      const [listData, statsData] = await Promise.all([
        api.listKnowledgePacks(listParams),
        api.getKnowledgeControlStats(),
      ])
      setItems(listData.items)
      setTotalCount(listData.total_count)
      setTotalPages(listData.total_pages)
      setStats(statsData)
    } catch (err) {
      if (err instanceof KnowledgeControlAuthError) {
        setUnauthorized(true)
        setItems([])
        setStats(null)
      } else {
        setError(err instanceof Error ? err.message : t('packs.failed'))
      }
    } finally {
      setLoading(false)
    }
  }

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key !== 'page') next.delete('page')
    setSearchParams(next)
  }

  function handleSaveToken(event: FormEvent) {
    event.preventDefault()
    if (!tokenDraft.trim()) return
    saveKnowledgeAdminToken(tokenDraft)
    setTokenDraft('')
    setHasToken(true)
    setUnauthorized(false)
  }

  async function handleCreate() {
    try {
      setCreating(true)
      setCreateError(null)
      const created = await api.createKnowledgePack({
        pack_id: createForm.pack_id.trim(),
        version: createForm.version.trim(),
        name: createForm.name.trim(),
        description: createForm.description.trim(),
        format_type: createForm.format_type.trim(),
      })
      setCreateOpen(false)
      navigate(`/knowledge-control/packs/${created.id}`)
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : t('packs.createFailed'))
    } finally {
      setCreating(false)
    }
  }

  if (hasToken === null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <LoadingSpinner size="lg" text={t('common.signingIn')} />
      </div>
    )
  }

  if (unauthorized || !hasToken) {
    return (
      <div style={{ maxWidth: '520px', margin: '0 auto', padding: '32px' }} data-testid="knowledge-packs-token-form">
        <h1 style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-2xl)' }}>
          {t('nav.knowledgePacks')}
        </h1>
        <p style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', color: 'var(--reviewer-text-secondary)' }}>
          {t('packs.tokenLead')}
        </p>
        <form onSubmit={handleSaveToken} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)' }}>
            {t('packs.tokenLabel')}
            <input
              type="password"
              value={tokenDraft}
              onChange={(event) => setTokenDraft(event.target.value)}
              autoComplete="off"
              data-testid="knowledge-packs-token-input"
              style={{
                display: 'block',
                width: '100%',
                marginTop: '8px',
                padding: '10px 12px',
                border: '1px solid var(--reviewer-border)',
                background: 'var(--reviewer-bg-primary)',
                color: 'var(--reviewer-text)',
                fontFamily: 'var(--reviewer-font-mono)',
              }}
            />
          </label>
          <Button type="submit" variant="primary" disabled={!tokenDraft.trim()}>
            {t('packs.authenticate')}
          </Button>
        </form>
      </div>
    )
  }

  if (loading && items.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <LoadingSpinner size="lg" text={t('packs.loading')} />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '32px' }}>
        <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', color: 'var(--reviewer-danger)' }}>
          {t('packs.failed')}
        </h2>
        <p style={{ fontFamily: 'var(--reviewer-font-mono)' }}>{error}</p>
        <Button onClick={() => void loadQueue()} variant="danger">{t('common.retry')}</Button>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }} data-testid="knowledge-packs-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-2xl)' }}>
            {t('nav.knowledgePacks')}
          </h1>
          <p style={{ marginTop: '8px', fontFamily: 'var(--reviewer-font-mono)', color: 'var(--reviewer-text-secondary)', fontSize: 'var(--reviewer-text-sm)' }}>
            {t('packs.lead')}
          </p>
          <Link to="/knowledge-control" style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: '12px', color: 'var(--reviewer-primary)' }}>
            {t('packs.backControl')}
          </Link>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button onClick={() => setCreateOpen(true)} variant="primary" size="sm">
            {t('packs.create')}
          </Button>
          <Button
            onClick={() => {
              clearKnowledgeAdminToken()
              setHasToken(false)
              setUnauthorized(true)
            }}
            variant="ghost"
            size="sm"
          >
            {t('packs.clearToken')}
          </Button>
        </div>
      </div>

      {stats && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', fontFamily: 'var(--reviewer-font-mono)' }}>
          <Badge variant="info">{t('packs.total', { n: stats.total_packs })}</Badge>
          <Badge variant="success">{t('packs.active', { n: stats.active_packs })}</Badge>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <input
          type="search"
          placeholder={t('packs.search')}
          value={search}
          onChange={(event) => updateFilter('search', event.target.value)}
          data-testid="knowledge-packs-search"
          style={{
            flex: '1 1 220px',
            padding: '8px 12px',
            border: '1px solid var(--reviewer-border)',
            fontFamily: 'var(--reviewer-font-mono)',
            background: 'var(--reviewer-bg-primary)',
            color: 'var(--reviewer-text)',
          }}
        />
        <select
          value={status}
          onChange={(event) => updateFilter('status', event.target.value)}
          aria-label={t('packs.statusAria')}
          data-testid="knowledge-packs-status"
          style={{
            padding: '8px 12px',
            border: '1px solid var(--reviewer-border)',
            fontFamily: 'var(--reviewer-font-mono)',
            background: 'var(--reviewer-bg-primary)',
            color: 'var(--reviewer-text)',
          }}
        >
          {STATUS_VALUES.map((value) => (
            <option key={value || 'all'} value={value}>
              {value ? lifecycleLabel(locale, value) : t('life.all')}
            </option>
          ))}
        </select>
        <select
          value={tag}
          onChange={(event) => updateFilter('tag', event.target.value)}
          aria-label={t('packs.groupAria')}
          data-testid="knowledge-packs-group"
          style={{
            padding: '8px 12px',
            border: '1px solid var(--reviewer-border)',
            fontFamily: 'var(--reviewer-font-mono)',
            background: 'var(--reviewer-bg-primary)',
            color: 'var(--reviewer-text)',
          }}
        >
          {GROUP_VALUES.map((value) => (
            <option key={value || 'all-groups'} value={value}>
              {value ? tagLabel(locale, value) : t('tag.all')}
            </option>
          ))}
        </select>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title={t('packs.empty')}
          description={search || status || tag ? t('packs.emptyFilter') : t('packs.emptyCreate')}
        />
      ) : (
        <div data-testid="knowledge-packs-table" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--reviewer-font-mono)' }}>
            <thead>
              <tr>
                {[
                  t('packs.colPack'),
                  t('packs.colStatus'),
                  t('packs.colValidation'),
                  t('packs.colMembers'),
                  t('packs.colFormat'),
                  t('packs.colTags'),
                ].map((header) => (
                  <th
                    key={header}
                    style={{
                      textAlign: 'left',
                      borderBottom: '1px solid var(--reviewer-border)',
                      padding: '10px 8px',
                      fontSize: '11px',
                      color: 'var(--reviewer-text-tertiary)',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/knowledge-control/packs/${item.id}`)}
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ padding: '12px 8px' }}>
                    <div>{packDisplayName(locale, item.pack_id, item.name)}</div>
                    <div style={{ fontSize: '11px', color: 'var(--reviewer-text-tertiary)' }}>
                      {item.pack_id}@{item.version}
                    </div>
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    <Badge variant={statusTone(item.status)}>{lifecycleLabel(locale, item.status)}</Badge>
                  </td>
                  <td style={{ padding: '12px 8px' }}>{validationLabel(locale, item.validation_status)}</td>
                  <td style={{ padding: '12px 8px' }}>{item.member_count}</td>
                  <td style={{ padding: '12px 8px' }}>{formatLabel(locale, item.format_type)}</td>
                  <td style={{ padding: '12px 8px', fontSize: '11px', color: 'var(--reviewer-text-tertiary)' }}>
                    {(item.tags || []).slice(0, 3).map((tag) => tagLabel(locale, tag)).join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => updateFilter('page', String(page - 1))}>
            {t('packs.prev')}
          </Button>
          <span style={{ fontFamily: 'var(--reviewer-font-mono)', alignSelf: 'center' }}>
            {t('packs.page', { page, total: totalPages, count: totalCount })}
          </span>
          <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => updateFilter('page', String(page + 1))}>
            {t('packs.next')}
          </Button>
        </div>
      )}

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title={t('packs.createTitle')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontFamily: 'var(--reviewer-font-mono)' }}>
          {createError && <p style={{ color: 'var(--reviewer-danger)' }}>{createError}</p>}
          {(['pack_id', 'version', 'name', 'format_type'] as const).map((field) => (
            <label key={field}>
              {field === 'pack_id'
                ? t('packs.fieldPackId')
                : field === 'version'
                  ? t('packs.fieldVersion')
                  : field === 'name'
                    ? t('packs.fieldName')
                    : t('packs.fieldFormat')}
              <input
                value={createForm[field]}
                onChange={(event) => setCreateForm({ ...createForm, [field]: event.target.value })}
                data-testid={`knowledge-packs-create-${field}`}
                style={{
                  display: 'block',
                  width: '100%',
                  marginTop: '4px',
                  padding: '8px',
                  border: '1px solid var(--reviewer-border)',
                  background: 'var(--reviewer-bg-primary)',
                  color: 'var(--reviewer-text)',
                  fontFamily: 'var(--reviewer-font-mono)',
                }}
              />
            </label>
          ))}
          <label>
            {t('packs.fieldDescription')}
            <textarea
              value={createForm.description}
              onChange={(event) => setCreateForm({ ...createForm, description: event.target.value })}
              data-testid="knowledge-packs-create-description"
              style={{
                display: 'block',
                width: '100%',
                minHeight: '80px',
                marginTop: '4px',
                padding: '8px',
                border: '1px solid var(--reviewer-border)',
                background: 'var(--reviewer-bg-primary)',
                color: 'var(--reviewer-text)',
                fontFamily: 'var(--reviewer-font-mono)',
              }}
            />
          </label>
          <Button
            variant="primary"
            disabled={creating || !createForm.pack_id || !createForm.name}
            onClick={() => void handleCreate()}
          >
            {creating ? t('packs.creating') : t('packs.createDraft')}
          </Button>
        </div>
      </Modal>
    </div>
  )
}
